import { showHttpError, showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createEntityAdapter } from '@reduxjs/toolkit'

import { historyPush } from 'actions/core/navigation'
import { resetData as resetScopeDropdownData } from 'actions/home_bar/scope_dropdown'
import { upgradeSubscription } from 'components/billing/use_upgrade_subscription'
import { convertETItemToETListItem, removeEventTypeFromListCachesData, updateEventTypeCacheData } from 'features/event_type_editor'
import { rootApi } from 'root-api'
import { getFeatureKeyFromError } from 'services/billing'

import { eventTypeSelector, selectedEventTypeIdsSelector, selectedEventTypesSelector } from './selectors'
import {
  addEventTypes,
  deselectAllEventTypes,
  setDeleteConfirmationPopupOpen,
  setVisibleEventTypeIds,
  openExportDelayedPopup
} from './slice'

const getBaseUrl = ({ ownerType, ownerId, eventTypeId }) => ({
  Organization: `/api/organization/event_types/${eventTypeId}`,
  Page: `/api/pages/${ownerId}/event_types/${eventTypeId}`,
  User: `/api/users/${ownerId}/event_types/${eventTypeId}`
}[ownerType])

const updatePath = (action, { id, type, owner_type: ownerType, owner_id: ownerId }) => {
  if (type === 'ManagedEventType') {
    return `/api/organization/managed_event_types/${id}/${action}`
  }
  if (ownerType === 'Organization') {
    return `/api/organization/event_types/${id}/${action}`
  }
  if (ownerType === 'Page') {
    return `/api/pages/${ownerId}/event_types/${id}/${action}`
  }

  return `/api/users/${ownerId}/event_types/${id}/${action}`
}

const getProfilesUrl = ({ scopeType, scopeId }) => {
  switch (scopeType) {
    case 'user_single':
      return `/api/users/${scopeId}/event_types`
    case 'page_single':
      return `/api/teams/${scopeId}/event_types`
    case 'user_group':
      return `/api/user_groups/${scopeId}/event_types`
    default:
      return '/api/users/me/event_types'
  }
}

const getEventTypesUrl = ({ profileType, profileId, userId }) => {
  switch (profileType) {
    case 'Page':
      return `/api/teams/${profileId}/event_types`
    case 'User':
      return `/api/users/${profileId}/event_types`
    case 'Organization':
      return `/api/users/${userId}/event_types`
    default:
      throw Error('Wrong profile type')
  }
}

const getEventTypesScope = (profileType) => {
  switch (profileType) {
    case 'Page':
      return 'page_single'
    case 'User':
      return 'personal'
    case 'Organization':
      return 'participated_organization'
    default:
      throw Error('Wrong profile type')
  }
}

const profilesAdapter = createEntityAdapter({
  selectId: (instance) => `${instance.type}::${instance.id}`
})

export const updateSchedulingFetchEventTypesCacheData = ({ api, scopeType, profilePage, uuid, data }) => (
  api.util.updateQueryData('schedulingFetchEventTypes', { scopeType, profilePage }, (draft) => {
    draft[uuid] = { ...draft[uuid], ...data }
  })
)

export const {
  selectById: selectProfileById,
  selectIds: selectProfileIds
} = profilesAdapter.getSelectors()

const api = rootApi.enhanceEndpoints({ addTagTypes: [
  'MEETING_TYPES_LIST',
  'PROFILE_MEETING_TYPES_LIST',
  'SINGLE-USE-LINKS',
  'SINGLE-USE-LINKS-PARTIAL-LIST',
  'MEETING-POLLS',
  'MEETING-POLLS-PARTIAL-LIST',
  'WIZARD_METADATA'
] }).injectEndpoints({
  endpoints: (build) => ({
    schedulingActivateEventType: build.mutation({
      query: ({ id }) => ({ getState }) => {
        const state = getState()
        const eventType = eventTypeSelector(id)(state)

        return {
          url: updatePath('activate', eventType),
          method: 'put'
        }
      },
      invalidatesTags: ['WIZARD_METADATA', 'EVENT_TYPES_COLLECTION'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ id, scopeType, profilePage, uuid, openedEventTypeId }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const data = { id, active: true }

          if (String(id) === String(openedEventTypeId)) {
            updateEventTypeCacheData({ api, state, dispatch, data })
          }

          dispatch(updateSchedulingFetchEventTypesCacheData({ api, scopeType, profilePage, uuid, data }))
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch,
            featureKey,
            onSuccess: () => (
              dispatch(api.endpoints.schedulingActivateEventType.initiate({ id, scopeType, profilePage, uuid, openedEventTypeId }))
            )
          })
        }
      }
    }),
    schedulingDeactivateEventType: build.mutation({
      query: ({ id }) => ({ getState }) => {
        const state = getState()
        const eventType = eventTypeSelector(id)(state)

        return {
          url: updatePath('deactivate', eventType),
          method: 'put'
        }
      },
      invalidatesTags: ['WIZARD_METADATA', 'EVENT_TYPES_COLLECTION'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ id, scopeType, profilePage, uuid, openedEventTypeId }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const data = { id, active: false }

          if (String(id) === String(openedEventTypeId)) {
            updateEventTypeCacheData({ api, state, dispatch, data })
          }

          dispatch(updateSchedulingFetchEventTypesCacheData({ api, scopeType, profilePage, uuid, data }))
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch,
            featureKey,
            onSuccess: () => (
              dispatch(api.endpoints.schedulingDeactivateEventType.initiate({ id, scopeType, profilePage, uuid, openedEventTypeId }))
            )
          })
        }
      }
    }),
    schedulingDeleteEventType: build.mutation({
      query: ({ ownerId, ownerType, eventTypeId }) => ({ url: getBaseUrl({ ownerType, ownerId, eventTypeId }), method: 'DELETE' }),
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ eventTypeId, uuid, openedEventTypeId, closeEventTypeSidebar }, { queryFulfilled, dispatch, getState }) => {
        try {
          await queryFulfilled

          if (uuid) {
            removeEventTypeFromListCachesData({ api, state: getState(), dispatch, uuid })
          }

          if (String(eventTypeId) === String(openedEventTypeId)) {
            closeEventTypeSidebar()
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
      invalidatesTags: ['WIZARD_METADATA', 'EVENT_TYPES_COLLECTION']
    }),
    schedulingFetchProfiles: build.query({
      query: ({ scopeType, scopeId, queryParams }) => ({
        url: getProfilesUrl({ scopeType, scopeId }),
        params: {
          scope: scopeType,
          ...queryParams
        }
      }),
      transformResponse: (baseQueryReturnValue) => {
        const results = baseQueryReturnValue.results.map((profile) => (
          { ...profile, eventTypeIds: profile.event_types.map(({ uuid }) => uuid) })
        )

        return {
          pagination: baseQueryReturnValue.pagination,
          results: profilesAdapter.setAll(profilesAdapter.getInitialState(), results)
        }
      },
      providesTags: ['MEETING_TYPES_LIST'],
      keepUnusedDataFor: 0,
      extraOptions: {
        transformResponse: false
      },
      onQueryStarted: async ({ scopeType, queryParams }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled
          const { page = 1 } = queryParams

          const profiles = profilesAdapter.getSelectors().selectAll(response.results)
          const eventTypes = profiles.flatMap((profile) => profile.event_types)

          dispatch(api.util.upsertQueryData('schedulingFetchEventTypes', {
            scopeType,
            profilePage: Number(page)
          }, Object.fromEntries(eventTypes.map((et) => [et.uuid, et]))))

          dispatch(addEventTypes(eventTypes))
          dispatch(setVisibleEventTypeIds(eventTypes.map(({ id }) => id)))
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    schedulingFetchEventTypes: build.query({
      query: ({ profileType, profileId, userId, page, filterTerm, participatedUserId }) => ({
        url: getEventTypesUrl({ profileType, profileId, userId }),
        params: {
          filter_term: filterTerm,
          participated_user_id: participatedUserId,
          scope: getEventTypesScope(profileType),
          page,
          format: 'collection'
        }
      }),
      serializeQueryArgs: ({ endpointName, queryArgs: { profilePage, scopeType } }) => (
        `${endpointName}(${JSON.stringify({ profilePage, scopeType })})`
      ),
      merge: (currentCacheData, responseData) => {
        const eventTypes = responseData.results ? responseData.results : Object.values(responseData)

        if (eventTypes) {
          eventTypes.forEach((newEventType) => {
            currentCacheData[newEventType.uuid] = newEventType
          })
        }
      },
      keepUnusedDataFor: 0,
      providesTags: ['PROFILE_MEETING_TYPES_LIST'],
      extraOptions: {
        transformResponse: false,
      },
      onQueryStarted: async ({ scopeType, scopeId, queryParams, profileType, profileId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled
          const eventTypes = response.results ? response.results : Object.values(response)
          if (eventTypes && eventTypes.length) {
            dispatch(api.util.updateQueryData('schedulingFetchProfiles', { scopeType, scopeId, queryParams }, (draft) => {
              // Next Page (Show More) of ETs is stored in Profile data store
              draft.results.entities[`${profileType}::${profileId}`].next_page = response.pagination.next_page
              draft.results.entities[`${profileType}::${profileId}`].next_page_count = response.pagination.next_page_count
              draft.results.entities[`${profileType}::${profileId}`].eventTypeIds.push(...eventTypes.map(({ uuid }) => uuid))
            }))

            dispatch(addEventTypes(eventTypes))
            dispatch(setVisibleEventTypeIds(eventTypes.map(({ id }) => id)))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    schedulingOrderEventTypes: build.mutation({
      query: ({ profileId, profileType, userId, newEventTypesPositions }) => {
        const positions = newEventTypesPositions.reduce((object, { id: uuid }, index) => {
          object[index] = uuid
          return object
        }, {})

        return {
          url: `${getEventTypesUrl({ profileType, profileId, userId })}/order`,
          method: 'PUT',
          data: { positions }
        }
      },
      onQueryStarted: async ({
        scopeType, profilePage, scopeId, queryParams, profileType, profileId, newEventTypesPositions
      }, { queryFulfilled, dispatch }) => {
        let backupEvenTypesOrder = []
        if (newEventTypesPositions && newEventTypesPositions.length) {
          dispatch(api.util.updateQueryData('schedulingFetchProfiles', { scopeType, scopeId, queryParams }, (draft) => {
            backupEvenTypesOrder = selectProfileById(draft.results, `${profileType}::${profileId}`)?.eventTypeIds
            draft.results.entities[`${profileType}::${profileId}`].eventTypeIds = newEventTypesPositions.map(({ id: uuid }) => uuid)
          }))
        }

        try {
          const { data: { ordered } } = await queryFulfilled

          if (ordered) {
            // Update cached ETs positions
            dispatch(api.util.updateQueryData('schedulingFetchEventTypes', { scopeType, profilePage }, (draft) => {
              newEventTypesPositions.forEach(({ id: uuid }, index) => {
                draft[uuid].position = index
              })
            }))
          } else {
            dispatch(api.util.updateQueryData('schedulingFetchProfiles', { scopeType, scopeId, queryParams }, (draft) => {
              draft.results.entities[`${profileType}::${profileId}`].eventTypeIds = backupEvenTypesOrder
            }))
          }
        } catch (e) {
          dispatch(api.util.updateQueryData('schedulingFetchProfiles', { scopeType, scopeId, queryParams }, (draft) => {
            draft.results.entities[`${profileType}::${profileId}`].eventTypeIds = backupEvenTypesOrder
          }))

          dispatch(showHttpError(e.error))
        }
      }
    }),
    schedulingRemoveTeam: build.mutation({
      query: ({ id }) => ({ url: `/api/pages/${id}`, method: 'DELETE' }),
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ scope }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
          if (scope === 'team' || scope === 'page_single') {
            dispatch(showSuccess(I18n.t('publisher.flash.event_types.delete_team')))
            dispatch(resetScopeDropdownData())
            dispatch(historyPush('/event_types/team'))
          } else {
            dispatch(api.util.invalidateTags(['SCHEDULING_PROFILE_EVENT_TYPE_LIST', 'PROFILE_MEETING_TYPES_LIST']))
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    schedulingSaveEventTypeInternalNote: build.mutation({
      query: ({ ownerId, ownerType, eventTypeId, internalNote }) => (
        { url: `${getBaseUrl({ ownerType, ownerId, eventTypeId })}/internal_note`, method: 'PUT', data: { internalNote } }
      ),
      onQueryStarted: async ({ eventTypeId, scopeType, profilePage, openedEventTypeId }, { queryFulfilled, dispatch, getState }) => {
        try {
          const { data: response } = await queryFulfilled

          const state = getState()
          const data = { id: eventTypeId, internal_note: response?.internal_note }

          if (String(eventTypeId) === String(openedEventTypeId)) {
            updateEventTypeCacheData({ api, state, dispatch, data })
          }

          dispatch(updateSchedulingFetchEventTypesCacheData({ api, scopeType, profilePage, uuid: response.uuid, data }))
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
      extraOptions: {
        transformResponse: false,
      }
    }),
    schedulingUpdateEventTypeAttributes: build.mutation({
      query: ({ ownerId, ownerType, eventTypeId, attributes }) => ({
        url: getBaseUrl({ ownerType, ownerId, eventTypeId }),
        method: 'PATCH',
        data: attributes
      }),
      onQueryStarted: async (
        { scopeType, profilePage, successMessage },
        { queryFulfilled, dispatch, getState }
      ) => {
        try {
          const { data: response } = await queryFulfilled

          const state = getState()
          const listData = convertETItemToETListItem(response)

          // Keep fetchEventType + list in sync when language/visibility changes from the card dropdown.
          // Full response (not partial attributes) so editor rail shows updated name/locale. See updateEventTypeCacheData tests.
          updateEventTypeCacheData({ api, state, dispatch, data: response })

          dispatch(updateSchedulingFetchEventTypesCacheData({ api, scopeType, profilePage, uuid: response.uuid, data: listData }))

          if (successMessage) dispatch(showSuccess(successMessage))
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
      extraOptions: {
        transformRequest: false,
        transformResponse: false,
        handleErrors: false
      }
    }),
    schedulingCloneEventType: build.mutation({
      query: ({ ownerId, ownerType, eventTypeId }) => (
        { url: `${getBaseUrl({ ownerType, ownerId, eventTypeId })}/clone`, method: 'POST' }
      ),
      invalidatesTags: ['WIZARD_METADATA'],
      onQueryStarted: async ({ onSuccess, shouldInvalidateTags }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled

          if (onSuccess) onSuccess(data)
          if (shouldInvalidateTags) dispatch(api.util.invalidateTags(['PROFILE_MEETING_TYPES_LIST', 'MEETING_TYPES_LIST']))
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      }
    }),
    schedulingBulkActivateEventTypes: build.mutation({
      query: () => ({ getState }) => {
        const state = getState()
        const ids = selectedEventTypeIdsSelector(state)

        return {
          url: '/api/event_types/bulk_actions/activate',
          method: 'put',
          data: { ids }
        }
      },
      invalidatesTags: ['PROFILE_MEETING_TYPES_LIST', 'MEETING_TYPES_LIST', 'WIZARD_METADATA', 'EVENT_TYPES_COLLECTION'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ openedEventTypeId }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const selectedEventTypes = selectedEventTypesSelector(state)

          if (selectedEventTypes.map(({ id }) => id).includes(openedEventTypeId)) {
            updateEventTypeCacheData({ api, state, dispatch, data: { id: openedEventTypeId, active: true } })
          }

          dispatch(showSuccess(I18n.t('publisher.flash.event_types.turned_on',
            { count: selectedEventTypes.filter((et) => !et.active).length })))
          dispatch(deselectAllEventTypes())
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch, featureKey, onSuccess: () => dispatch(api.endpoints.schedulingBulkActivateEventTypes.initiate({ openedEventTypeId }))
          })
        }
      }
    }),
    schedulingBulkDeactivateEventTypes: build.mutation({
      query: () => ({ getState }) => {
        const state = getState()
        const ids = selectedEventTypeIdsSelector(state)

        return {
          url: '/api/event_types/bulk_actions/deactivate',
          method: 'put',
          data: { ids }
        }
      },
      invalidatesTags: ['PROFILE_MEETING_TYPES_LIST', 'MEETING_TYPES_LIST', 'WIZARD_METADATA', 'EVENT_TYPES_COLLECTION'],
      extraOptions: {
        handleErrors: false
      },
      onQueryStarted: async ({ openedEventTypeId }, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled

          const state = getState()
          const selectedEventTypes = selectedEventTypesSelector(state)

          if (selectedEventTypes.map(({ id }) => id).includes(openedEventTypeId)) {
            updateEventTypeCacheData({ api, state, dispatch, data: { id: openedEventTypeId, active: false } })
          }

          dispatch(showSuccess(I18n.t('publisher.flash.event_types.turned_off',
            { count: selectedEventTypes.filter((et) => et.active).length })))
          dispatch(deselectAllEventTypes())
        } catch (e) {
          const featureKey = getFeatureKeyFromError(e.error)
          if (!featureKey) {
            dispatch(showHttpError(e.error))
            return
          }

          upgradeSubscription({
            dispatch, featureKey, onSuccess: () => dispatch(api.endpoints.schedulingBulkDeactivateEventTypes.initiate())
          })
        }
      }
    }),
    schedulingBulkDeleteEventTypes: build.mutation({
      query: () => ({ getState }) => {
        const state = getState()
        const ids = selectedEventTypeIdsSelector(state)

        return {
          url: '/api/event_types/bulk_actions/destroy',
          method: 'post',
          data: { ids }
        }
      },
      onQueryStarted: async (_, { queryFulfilled, dispatch, getState }) => {
        const deletedUuids = selectedEventTypesSelector(getState())
          .map(({ uuid }) => uuid)
          .filter(Boolean)

        try {
          await queryFulfilled

          if (deletedUuids.length) {
            removeEventTypeFromListCachesData({ api, state: getState(), dispatch, uuids: deletedUuids })
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
      invalidatesTags: ['WIZARD_METADATA', 'EVENT_TYPES_COLLECTION'],
      extraOptions: {
        handleErrors: false,
        onSuccess: ({ openedEventTypeId, closeEventTypeSidebar }, { dispatch, getState }) => {
          const state = getState()
          const deletedIds = selectedEventTypeIdsSelector(state)

          dispatch(setDeleteConfirmationPopupOpen(false))
          dispatch(deselectAllEventTypes())

          if (deletedIds.includes(openedEventTypeId)) {
            closeEventTypeSidebar()
          }
        }
      }
    }),
    schedulingBulkImportAvailability: build.mutation({
      query: ({ sourceId, selectedMembershipIds }) => ({ getState }) => {
        const state = getState()
        const targetIds = selectedEventTypeIdsSelector(state)

        return {
          url: '/api/availability/event_types/import',
          method: 'post',
          data: {
            targetEventTypeIds: targetIds,
            sourceEventTypeId: sourceId,
            selectedMembershipIds
          }
        }
      },
      invalidatesTags: ['PROFILE_MEETING_TYPES_LIST', 'MEETING_TYPES_LIST'],
      extraOptions: {
        onSuccess: (_, { dispatch, getState }) => {
          const state = getState()
          const targetIds = selectedEventTypeIdsSelector(state)

          dispatch(showSuccess(I18n.t('publisher.event_types.availability.availability_updated_for', { count: targetIds.length })))
        }
      }
    }),
    schedulingFetchEventTypesPolicy: build.query({
      query: (params = {}) => ({
        url: '/api/event_types/policy',
        params,
        validateStatus: (response) => (['200', '404'].includes(`${response}`))
      })
    }),
    clearReservedTimes: build.mutation({
      query: ({ userId, eventTypeId }) => ({
        url: `/api/users/${userId}/adhoc_event_types/${eventTypeId}/clear_reserved_times`,
        method: 'PUT'
      }),
      invalidatesTags: ['MEETING-POLLS', 'MEETING-POLLS-PARTIAL-LIST', 'SINGLE-USE-LINKS', 'SINGLE-USE-LINKS-PARTIAL-LIST'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled
        dispatch(
          showSuccess(I18n.t('publisher.scheduling.flash.clear_reserved_times_success'))
        )
      }
    }),
    getEventTypeActivityReport: build.query({
      query: ({ eventTypeId }) => ({
        url: '/api/organization/event_type_distribution/export',
        params: { event_type_id: eventTypeId },
        method: 'POST'
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled

          if (data.location === 'inline') {
            window.open(data.url, '_self')
          } else if (data.location === 'email') {
            dispatch(openExportDelayedPopup())
          }
        } catch (e) {
          dispatch(showHttpError(e.error))
        }
      },
    })
  })
})

export const {
  useSchedulingActivateEventTypeMutation,
  useSchedulingDeactivateEventTypeMutation,
  useSchedulingDeleteEventTypeMutation,
  useSchedulingFetchProfilesQuery,
  useLazySchedulingFetchEventTypesQuery,
  useSchedulingRemoveTeamMutation,
  useSchedulingCloneEventTypeMutation,
  useSchedulingSaveEventTypeInternalNoteMutation,
  useSchedulingUpdateEventTypeAttributesMutation,
  useSchedulingBulkActivateEventTypesMutation,
  useSchedulingBulkDeactivateEventTypesMutation,
  useSchedulingBulkDeleteEventTypesMutation,
  useSchedulingBulkImportAvailabilityMutation,
  useSchedulingFetchEventTypesPolicyQuery,
  useSchedulingOrderEventTypesMutation,
  useClearReservedTimesMutation,
  useLazyGetEventTypeActivityReportQuery,
  endpoints: { schedulingFetchEventTypes: { useQueryState: useFetchEventTypesQueryState } }
} = api
